import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, AppBar, Toolbar, IconButton, Typography, Button, Icon } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LibraryIcon from '@mui/icons-material/Book';
import useStyles from './TaskCreate.styles';
import { get, set, size, last, sortBy } from 'global/lib/lodash';
import Form from 'global/components/form';
import Styles, { useScreen, Loader } from 'global/styles';
import Alert from '@mui/material/Alert';
import clsx from 'clsx';
import TaskTag from './TaskTag';
import ScoreIcon from 'global/components/ScoreIcon';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import qs from 'qs';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useAccessEdition } from 'global/utils/edition';
import Link from 'react-router-dom/Link';
import { UploadFiles } from 'redux/actions/imageUploader';
import { getDisplayValue } from 'global/utils/getDisplayScore';
import { useUser } from 'global/hooks/useUser';
import { LI } from '../../report/Highlights';

export const RenderTag = ({ tag, showLink }) => {
  const classes = useStyles();
  const hasReportSection = get(tag, 'reportSection');
  const checkpointId = get(tag, 'checkpointId');
  const checklistId = get(tag, 'checklistId');

  const params = [
    { url: `/edition-documents` },
    { build: ['editionDocuments'], params: { filter: { documentable_type: 'checklist', documentable_id: checklistId } }, showAlert: false, transformData: (d) => last(d) },
  ];

  const { data } = useRetrieveData(...params);
  const editionId = get(data, 'editionId');
  const bool = useAccessEdition(editionId);

  const getLink = (() => {
    if (bool) {
      return `/library/${editionId}/document/${get(data, 'classificationId')}${checkpointId ? `?hl=${checkpointId}` : ''}`;
    }
    return '';
  })();

  const superSet = get(data, 'superset');

  if (hasReportSection) {
    return (
      <>
        <LI li={hasReportSection} collapseContentOnly />
      </>
    );
  }

  const score = get(tag, 'score');
  const displayValue = getDisplayValue({ value: score }, get(tag, 'scorecardConfig', null));

  // checkpointId determines whether the score is a checkpoint score or a general observation (a checklist score)
  return (
    <>
      {superSet && <div className={classes.superText}>{get(superSet, 'name')}</div>}
      <div className={classes.tagContainer}>
        <p className={classes.tagText}>
          {!!checkpointId && <span className={classes.tagOption}>{displayValue}</span>}
          <span className={classes.tagBox}>
            <ScoreIcon center score={!!checkpointId ? score : undefined} />
          </span>
          <span className={classes.tagName}>{get(tag, 'name', '')}</span>
          <span className={classes.tagDescription}>{!!checkpointId ? get(tag, 'criteria') || get(tag, 'description') : `General Observation: ${get(tag, 'checklist.name')}`}</span>
        </p>
      </div>
      {showLink && getLink && (
        <div className={classes.tagLink}>
          <Typography className={classes.tagReference} variant="body2" component={Link} to={getLink}>
            <LibraryIcon />
            Show Reference Document
          </Typography>
        </div>
      )}
    </>
  );
};

export default (props) => {
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState();
  const [tag, setTag] = useState();
  const [formState, setFormState] = useState({});
  const [tagModal, setTagModal] = useState(false);
  const [tagName, setTagName] = useState();
  const { me } = useUser();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const classes = useStyles();
  const styles = Styles();
  const { isMobileXS } = useScreen();

  const usersList = useRetrieveData(
    { url: '/users' },
    {
      waitFor: get(props, 'realmItem.realm.id', null),
      build: ['users'],
      params: { filter: { content_realm_id: get(props, 'realmItem.realm.id') } },
      useCache: { reload: 1 },
    }
  );

  const reset = (formState = {}) => {
    setFormState(formState);
    setTagModal(false);
    setError();
    setTag();
    setFormLoading(false);
  };

  const handleClose = async (tsk) => {
    await props.onClose(tsk);
    reset();
  };

  const formatData = async (values) => {
    const relationships = {};

    let tresult;
    if (tag) {
      const selectedOption = get(tag, 'checkpoint.selectedOption');
      const cpo = get(tag, `checkpoint.checkpoint.checkpointOptions.${get(selectedOption, 'index')}`);
      tresult = await sendData({
        url: '/scorecard-scores',
        method: 'post',
        data: {
          data: {
            type: 'scorecard-scores',
            attributes: {
              scorecard_id: get(tag, 'scorecardId'),
              checklist_id: get(tag, 'checklist.id'),
              checkpoint_id: get(tag, 'checkpoint.checkpoint.id'),
              checkpoint_option_id: get(cpo, 'id'),
              criteria: [get(tag, 'checkpoint.checkpoint.description'), get(cpo, 'name')].filter(Boolean).join(' '),
              score: get(cpo, 'value', 0),
            },
          },
        },
      });
    }

    const attributes = {
      name: get(values, 'name'),
      description: get(values, 'description'),
      status: 0,
      realm_id: get(props, 'realmItem.realm.id'),
      scorecard_score_id: get(tresult, 'data.data.id'),
      checkpoint_id: get(tag, 'checkpoint.checkpoint.id'),
      images: get(formState, 'photos', [])
        .filter((img) => img.data)
        .map((p) => ({ ...p, name: p.path, caption: get(p, 'caption', '') })),
    };

    const assignees = get(formState, 'assignedTo');
    set(relationships, 'assignees', {
      data: size(assignees) ? assignees.map((e) => ({ type: 'users', role: 'assignee', id: e.id })) : [{ type: 'users', role: 'assignee', id: me.id }],
    });

    set(relationships, 'authors', {
      data: [{ type: 'users', role: 'author', id: get(me, 'id', '').toString() }],
    });

    const data = {
      data: {
        type: 'tasks',
        attributes,
        relationships,
      },
    };

    const tradeTypes = get(formState, 'tradeTypes');
    if (size(tradeTypes)) {
      set(relationships, 'tradeTypes', {
        data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } }))
      })
    }

    if (size(attributes.images)) {
      await UploadFiles(data);
    }

    return data;
  };

  const formSubmit = async (addAnother = false) => {
    try {
      setFormLoading(true);
      const data = await formatData(formState);
      const createdTask = await sendData({
        url: '/tasks',
        method: 'post',
        data,
      });
      const tsk = get(createdTask, 'data.data');
      const include =
        'images,assignees,viewers,authors,comments,comments.author,comments.images,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions,tradeTypes';
      const fullTask = await sendData(
        {
          url: `/tasks/${tsk.id}?${qs.stringify({ include })}`,
          method: 'get',
        },
        { normalize: { build: ['tasks'] } }
      );
      dispatch(addNewTask(get(fullTask, '0')));
      if (addAnother) {
        const assignedTo = get(formState, 'assignedTo');
        reset({ assignedTo });
      } else {
        await handleClose(tsk);
      }
      setFormLoading(false);
    } catch (err) {
      setFormLoading(false);
      setError(`${err.message}. Please try again.`);
    }
  };

  const formUpdate = (vals) => {
    for (let k in vals) {
      set(formState, k, vals[k]);
    }
    setFormState({ ...formState });
  };

  const clearTag = () => {
    setTag();
    setTagName();
    if (tagName === get(formState, 'name')) {
      formRef.current.updateState('name', '');
    }
  };

  const onTagSelected = (tags) => {
    setTag(tags);
    if (!get(formState, 'name')) {
      const clname = get(tags, 'checklist.name');
      const cpname = get(tags, 'checkpoint.checkpoint.name');
      const name = clname + (cpname ? `: ${cpname}` : '');
      setTagName(name);
      formRef.current.updateState('name', name);
    }
  };

  const saveEnabled = formState.name && (!!formState.description || !!size(formState.photos));

  return (
    <Dialog className={classes.dialog} fullScreen disableScrollLock disableEscapeKeyDown open={props.open} onClose={() => handleClose()}>
      <AppBar color="secondary" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => handleClose()}
            aria-label="Close"
            size="large">
            <CloseIcon />
          </IconButton>

          <div className={classes.barTitle}>
            <Typography variant="h5" className={classes.title}>
              Create Task
            </Typography>
          </div>

          <Typography variant="h6" className={classes.status}>
            {get(props, 'realmItem.realmName', '')}
            <Icon className={classes.statusIcon}>place</Icon>
          </Typography>
        </Toolbar>
      </AppBar>

      {!formLoading && (
        <DialogContent className={classes.container}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <div className={classes.buttonContainer}>
            <Button
              variant="outlined"
              startIcon={<Icon>{tag ? 'close' : 'label'}</Icon>}
              onClick={tag ? () => clearTag() : () => setTagModal(true)}
              className={clsx(classes.tagButton, tag && classes.tagRemove)}
            >
              {tag ? 'REMOVE LINK' : 'ADD LINK'}
            </Button>

            {tag && <RenderTag tag={tag} />}
          </div>

          <Form
            loading={formLoading}
            showAllScreens={true}
            formStyle={classes.formStyle}
            onSubmit={formSubmit}
            onFormState={formUpdate}
            ref={formRef}
            useHeight={false}
            screens={[
              {
                title: '',
                titleStyle: classes.titleStyle,
                screenContainerStyle: isMobileXS ? classes.screenContainerMobile : classes.screenContainer,
                screenContainer: {
                  props: {
                    className: classes.screenWidth,
                  },
                },
                inputs: [
                  {
                    id: 'name',
                    type: 'TextField',
                    defaultValue: () => get(formState, 'name'),
                    props: {
                      // label: 'Title',
                      placeholder: 'Enter Title',
                    },
                  },
                  {
                    id: 'description',
                    type: 'TextField',
                    defaultValue: () => get(formState, 'description'),
                    props: {
                      // label: 'Description',
                      placeholder: 'Describe your task',
                      rows: 5,
                      multiline: true,
                    },
                  },
                  {
                    id: 'assignedTo',
                    type: 'UserSelect',
                    defaultValue: () => get(formState, 'assignedTo', []),
                    data: () => usersList.data,
                    props: {
                      label: 'Assigned To',
                    },
                  },
                  {
                    id: 'tradeTypes',
                    type: 'TradeSelect',
                    defaultValue: () => get(formState, 'tradeTypes', []),
                    props: {
                      label: 'Trade Types',
                      checklist: get(tag, 'checklist'),
                    },
                  },
                  {
                    id: 'photos',
                    type: 'DropArea',
                    defaultValue: () => sortBy(get(formState, 'photos', []), 'orderColumn'),
                    props: {
                      label: 'Images',
                      sortable: true,
                    },
                  },
                ],
              },
            ]}
          />
        </DialogContent>
      )}

      {!formLoading && (
        <DialogActions className={classes.submitButtonContainer}>
          <Button
            startIcon={
              <div className={classes.doubleIcon}>
                <Icon>check_plus</Icon>
                <Icon className={classes.plusIcon}>add</Icon>
              </div>
            }
            variant={'contained'}
            color="secondary"
            onClick={() => formSubmit(true)}
            disabled={!saveEnabled}
            classes={{ root: classes.button }}
          >
            <span className={classes.submitBtnText}>Save & Add</span>
          </Button>

          <Button
            startIcon={<Icon style={{ fontSize: 30 }}>check</Icon>}
            variant={'contained'}
            color="secondary"
            onClick={() => formSubmit()}
            disabled={!saveEnabled}
            classes={{ root: classes.button }}
          >
            <span className={classes.submitBtnText}>Save & Close</span>
          </Button>
        </DialogActions>
      )}

      {formLoading && (
        <div className={clsx(classes.container, styles.centerColumn)}>
          <Loader size={40} />
        </div>
      )}

      <TaskTag open={tagModal} onTagSelected={onTagSelected} onClose={() => setTagModal(false)} />
    </Dialog>
  );
};
